import { createDesk, type Desk, type DeskApi } from "./desk/controller.ts";
import { closeQualityPopout } from "./ui/quality.tsx";
import type { openBrowserMedia } from "./desk/transport.ts";
import { SESSION_REASON_PREFIX, readStoredSessionReason, sessionReasonKey, storedSessionReason } from "./sessionReason.ts";
import {
  SESSION_NAME_PREFIX,
  deviceTitleName,
  readStoredDeviceName,
  sessionNameKey,
  sessionWindowTitle,
  storedDeviceName,
} from "./sessionTitle.ts";

export type SessionLaunch = {
  deviceId: string;
  accessMode: "Attended" | "Unattended";
  features: { clipboard: boolean; fileTransfer: boolean; recording: boolean };
  displayName?: string;
  hostname?: string;
};

export type AdminPage = "overview" | "devices" | "users" | "roles" | "audit" | "recording";

export type PortalDocument =
  | { role: "devices" }
  | { role: "admin"; page: AdminPage }
  | { role: "recordings" }
  | { role: "session"; launch: SessionLaunch }
  | { role: "stealth"; sessionId: string }
  | { role: "unreadable-session"; message: string };

export type SessionOpen = { ok: true } | { ok: false; reason: "blocked" };

export type SessionOpenInput = SessionLaunch & { reason?: string };

export type SessionWindows = {
  open(launch: SessionOpenInput): SessionOpen;
  closeAll(): void;
};

export type SessionBoot =
  | { role: "session"; desk: Desk; deviceId: string; deviceName: string }
  | { role: "already-started" }
  | { role: "unreadable-session"; message: string };

const LAUNCH_KEY = "wylo-rc-session-launch";
const LEGACY_SESSION_REASON_KEY = "wylo-rc-session-reason";
const POPUP_FEATURES = "popup,width=1280,height=800";
const INVALID_SESSION = "The session address is invalid.";
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type NameStorage = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem?(key: string): void;
  length?: number;
  key?(index: number): string | null;
};

type SessionHost = {
  location: { pathname: string; search: string };
  sessionStorage: NameStorage;
  opener: unknown;
  name?: string;
  document?: { title: string };
  addEventListener(type: string, listener: (event: Event) => void): void;
};

type Popup = { close(): void };

const booted = new WeakMap<object, SessionBoot>();

function isSessionPath(pathname: string): boolean {
  return pathname === "/session" || pathname === "/session/";
}

function trimPath(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith("/")) return pathname.slice(0, -1);
  return pathname;
}

const SETTINGS_PAGE: Record<string, AdminPage> = {
  "/admin": "overview",
  "/admin/overview": "overview",
  "/settings": "overview",
  "/settings/overview": "overview",
  "/admin/devices": "devices",
  "/settings/devices": "devices",
  "/admin/users": "users",
  "/settings/users": "users",
  "/admin/roles": "roles",
  "/settings/roles": "roles",
  "/admin/audit": "audit",
  "/audit": "audit",
  "/admin/session-policies": "recording",
  "/admin/recording": "recording",
  "/admin/groups": "recording",
  "/settings/session-policies": "recording",
  "/settings/recording": "recording",
  "/settings/groups": "recording",
  "/settings/policies": "recording",
};

const CANONICAL_SETTINGS: Record<Exclude<AdminPage, "audit">, string> = {
  overview: "/settings",
  devices: "/settings/devices",
  users: "/settings/users",
  roles: "/settings/roles",
  recording: "/settings/policies",
};

function adminPage(pathname: string): AdminPage | null {
  return SETTINGS_PAGE[trimPath(pathname)] ?? null;
}

export function canonicalSettingsPath(pathname: string): string | null {
  const path = trimPath(pathname);
  const page = adminPage(path);
  if (!page || page === "audit") return null;
  const target = CANONICAL_SETTINGS[page];
  return path === target ? null : target;
}

function isRecordingsPath(pathname: string): boolean {
  return pathname === "/recordings" || pathname === "/recordings/";
}

function flag(value: string | null): boolean | null {
  if (value === "1") return true;
  if (value === "0") return false;
  return null;
}

function accessMode(value: string | null): SessionLaunch["accessMode"] | null {
  if (value === "Attended" || value === "Unattended") return value;
  return null;
}

function launchQuery(launch: SessionLaunch): string {
  const clipboard = launch.features.clipboard ? "1" : "0";
  const fileTransfer = launch.features.fileTransfer ? "1" : "0";
  const recording = launch.features.recording ? "&recording=1" : "";
  return `deviceId=${launch.deviceId}&accessMode=${launch.accessMode}&clipboard=${clipboard}&fileTransfer=${fileTransfer}${recording}`;
}

function parseLaunch(search: string): SessionLaunch | null {
  const params = new URLSearchParams(search);
  const deviceId = params.get("deviceId");
  const mode = accessMode(params.get("accessMode"));
  const clipboard = flag(params.get("clipboard"));
  const fileTransfer = flag(params.get("fileTransfer"));
  const recordingParam = params.get("recording");
  const recording = recordingParam === null ? false : flag(recordingParam);
  if (!deviceId || !UUID.test(deviceId) || !mode || clipboard === null || fileTransfer === null || recording === null) return null;
  return { deviceId, accessMode: mode, features: { clipboard, fileTransfer, recording } };
}

export function readPortalDocument(location: { pathname: string; search: string }): PortalDocument {
  if (isSessionPath(location.pathname)) {
    const launch = parseLaunch(location.search);
    if (!launch) return { role: "unreadable-session", message: INVALID_SESSION };
    return { role: "session", launch };
  }
  if (trimPath(location.pathname) === "/stealth") {
    const sessionId = new URLSearchParams(location.search).get("sessionId");
    if (!sessionId || !UUID.test(sessionId)) return { role: "unreadable-session", message: INVALID_SESSION };
    return { role: "stealth", sessionId };
  }
  const page = adminPage(location.pathname);
  if (page) return { role: "admin", page };
  const verify = new URLSearchParams(location.search).get("wyloVerify");
  if (verify === "admin" || verify === "users-status") return { role: "admin", page: "users" };
  if (verify === "admin-devices") return { role: "admin", page: "devices" };
  if (verify === "overview") return { role: "admin", page: "overview" };
  if (verify === "roles") return { role: "admin", page: "roles" };
  if (verify === "audit") return { role: "admin", page: "audit" };
  if (verify === "recording") return { role: "admin", page: "recording" };
  if (isRecordingsPath(location.pathname)) return { role: "recordings" };
  return { role: "devices" };
}

function sessionUrl(launch: SessionLaunch, search: string): string {
  const verify = new URLSearchParams(search).get("wyloVerify");
  const base = `/session?${launchQuery(launch)}`;
  if (verify === null) return base;
  return `${base}&wyloVerify=${encodeURIComponent(verify)}`;
}

export function createSessionWindows(input?: {
  open?: (url: string, target: string, features: string) => Popup | null;
  search?: () => string;
  newId?: () => string;
  storage?: NameStorage;
}): SessionWindows {
  const open = input?.open ?? ((url, target, features) => window.open(url, target, features));
  const search = input?.search ?? (() => window.location.search);
  const newId = input?.newId ?? (() => crypto.randomUUID());
  const children: Popup[] = [];
  return {
    open(launch) {
      const target = `session-${newId()}`;
      const storage = input?.storage ?? sessionStorage;
      const nameKey = sessionNameKey(launch.deviceId);
      if (launch.displayName !== undefined || launch.hostname !== undefined) {
        storage.setItem(nameKey, storedDeviceName(launch.deviceId, launch.displayName ?? "", launch.hostname ?? ""));
      }
      const reasonKey = sessionReasonKey(launch.deviceId);
      if (launch.reason !== undefined) {
        storage.setItem(reasonKey, storedSessionReason(launch.deviceId, launch.reason, target));
      }
      const child = open(sessionUrl(launch, search()), target, POPUP_FEATURES);
      if (!child) {
        storage.removeItem?.(nameKey);
        storage.removeItem?.(reasonKey);
        return { ok: false, reason: "blocked" };
      }
      children.push(child);
      return { ok: true };
    },
    closeAll() {
      for (const child of children) child.close();
      children.length = 0;
    },
  };
}

function keptInPageCache(event: Event): boolean {
  if (!("persisted" in event)) return false;
  return event.persisted === true;
}

function bindUnload(host: SessionHost, desk: Desk) {
  const finish = () => {
    closeQualityPopout();
    void desk.end();
  };
  host.addEventListener("pagehide", (event) => {
    if (keptInPageCache(event)) return;
    finish();
  });
  host.addEventListener("beforeunload", () => {
    finish();
  });
}

export function bootSessionDocument(input: {
  host: SessionHost;
  api: DeskApi;
  apiBase?: string;
  openMedia?: typeof openBrowserMedia;
  saveFile?: (name: string, bytes: Uint8Array) => void;
  who?: () => string | null;
}): SessionBoot {
  const prior = booted.get(input.host);
  if (prior) return prior;
  const parsed = readPortalDocument(input.host.location);
  if (parsed.role !== "session") {
    return parsed.role === "unreadable-session" ? parsed : { role: "unreadable-session", message: INVALID_SESSION };
  }
  const canonical = launchQuery(parsed.launch);
  if (input.host.sessionStorage.getItem(LAUNCH_KEY) === canonical) return { role: "already-started" };
  const desk = createDesk({
    api: input.api,
    apiBase: input.apiBase,
    openMedia: input.openMedia,
    saveFile: input.saveFile,
    who: input.who,
  });
  const deviceName = deviceTitleName(takeSessionName(input.host, parsed.launch.deviceId));
  const boot: SessionBoot = { role: "session", desk, deviceId: parsed.launch.deviceId, deviceName };
  booted.set(input.host, boot);
  input.host.sessionStorage.setItem(LAUNCH_KEY, canonical);
  bindUnload(input.host, desk);
  const reason = takeSessionReason(input.host, parsed.launch.deviceId);
  input.host.opener = null;
  if (input.host.document) input.host.document.title = sessionWindowTitle(deviceName, "remote");
  void desk.start(parsed.launch, reason);
  return boot;
}

function purgeOtherDeviceKeysFromOwnStore(host: SessionHost, deviceId: string) {
  const storage = host.sessionStorage;
  if (storage.length === undefined || storage.key === undefined) return;
  const ownReasonKey = sessionReasonKey(deviceId);
  const ownNameKey = sessionNameKey(deviceId);
  const keys: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (key) keys.push(key);
  }
  for (const key of keys) {
    if (key.startsWith(SESSION_REASON_PREFIX) && key !== ownReasonKey) storage.removeItem?.(key);
    if (key.startsWith(SESSION_NAME_PREFIX) && key !== ownNameKey) storage.removeItem?.(key);
  }
}

function takeSessionReason(host: SessionHost, deviceId: string): string | undefined {
  purgeOtherDeviceKeysFromOwnStore(host, deviceId);
  host.sessionStorage.removeItem?.(LEGACY_SESSION_REASON_KEY);
  const opener = openerStorage(host.opener);
  opener?.removeItem?.(LEGACY_SESSION_REASON_KEY);

  const key = sessionReasonKey(deviceId);
  const own = host.sessionStorage.getItem(key);
  host.sessionStorage.removeItem?.(key);
  const fromOpener = opener?.getItem(key) ?? null;
  opener?.removeItem?.(key);
  return readStoredSessionReason(own ?? fromOpener, deviceId, host.name ?? null);
}

function takeSessionName(host: SessionHost, deviceId: string): { name: string; hostname: string } {
  const key = sessionNameKey(deviceId);
  const own = host.sessionStorage.getItem(key);
  host.sessionStorage.removeItem?.(key);
  const opener = openerStorage(host.opener);
  const fromOpener = opener?.getItem(key) ?? null;
  opener?.removeItem?.(key);
  return readStoredDeviceName(own ?? fromOpener, deviceId);
}

function openerStorage(opener: unknown): NameStorage | null {
  if (!opener || typeof opener !== "object" || !("sessionStorage" in opener)) return null;
  const storage = (opener as { sessionStorage?: NameStorage }).sessionStorage;
  if (!storage?.getItem) return null;
  return storage;
}
